import React, { useEffect, useState } from 'react';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import Button from '../ui/Button';

interface PaymentFormProps {
  onSuccess?: (paymentIntentId: string) => void;
  onError?: (message: string) => void;
  amountCents?: number;
  returnUrl?: string;
  onBackToBooking?: () => void;
}

const PaymentForm: React.FC<PaymentFormProps> = ({ onSuccess, onError, amountCents, returnUrl, onBackToBooking }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Post-redirect handling: if Stripe redirected back with a client_secret, retrieve the PaymentIntent status
  useEffect(() => {
    const handleRedirectResult = async () => {
      if (!stripe) return;
      const params = new URLSearchParams(window.location.search);
      const clientSecret = params.get('payment_intent_client_secret');
      if (!clientSecret) return;

      const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);
      if (error) {
        const message = error.message || 'Une erreur est survenue lors de la récupération du paiement.';
        setError(message);
        onError?.(message);
        return;
      }
      if (!paymentIntent) return;

      if (
        paymentIntent.status === 'succeeded' ||
        paymentIntent.status === 'processing' ||
        paymentIntent.status === 'requires_capture'
      ) {
        onSuccess?.(paymentIntent.id);
      } else if (paymentIntent.status === 'requires_payment_method') {
        const message = paymentIntent.last_payment_error?.message || 'Le paiement a été annulé ou a échoué.';
        setError(message);
        onError?.(message);
      }
    };

    handleRedirectResult();
  }, [stripe, onError, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: returnUrl || window.location.href,
        payment_method_data: {
          billing_details: {
            address: {
              country: 'FR', // Le pays est requis car nous l'avons masqué dans le formulaire
              postal_code: '75001', // Code postal par défaut car masqué aussi
            },
          },
        },
      },
      redirect: 'always',
    });

    if (error) {
      const message = error.message || 'Le paiement a échoué. Veuillez réessayer ou utiliser un autre moyen de paiement.';
      setError(message);
      onError?.(message);
      setSubmitting(false);
      return;
    }


    setSubmitting(false);
  };

  return (
    <div className="relative w-full max-w-2xl mx-auto">
      {submitting && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-white/70 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-3">
            <div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
            <p className="font-nunito text-neutral-ink text-sm">Traitement du paiement…</p>
            <p className="font-nunito text-neutral-body text-xs">Redirection vers la page de confirmation…</p>
          </div>
        </div>
      )}
      <form onSubmit={handleSubmit} className="space-y-6 w-full">
        <div className="bg-white rounded-xl border border-neutral-line p-4 w-full">
          <PaymentElement options={{ 
            layout: 'tabs',
            fields: {
              billingDetails: {
                address: {
                  country: 'never',
                  postalCode: 'never'
                }
              }
            }
          }} />
        </div>

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onBackToBooking && (
              <Button 
                type="button" 
                variant="secondary" 
                size="md" 
                onClick={onBackToBooking}
                disabled={submitting}
              >
                ← Retour
              </Button>
            )}
            <div className="text-neutral-body text-sm">
              {typeof amountCents === 'number' && (
                <span>Montant: <strong>{(amountCents / 100).toFixed(2)} €</strong></span>
              )}
            </div>
          </div>
          <Button type="submit" variant="primary" size="lg" disabled={!stripe || submitting}>
            {submitting ? 'Paiement en cours…' : 'Payer maintenant'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PaymentForm;
